import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { UserProfileDto, AuthTokens } from '@xtreed/academy-shared';

function sessionKey(name: string) {
  if (!import.meta.env.DEV) return name;
  const tenant = localStorage.getItem('xt:dev-tenant');
  return tenant ? `${name}:${tenant}` : name;
}

interface AuthState {
  user: UserProfileDto | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setUser: (user: UserProfileDto | null) => void;
  setTokens: (tokens: AuthTokens) => void;
  login: (user: UserProfileDto, tokens: AuthTokens) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setUser: (user) => set({ user }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        }),

      login: (user, tokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'xtreed-auth',
      storage: createJSONStorage(() => ({
        getItem: (name) => localStorage.getItem(sessionKey(name)),
        setItem: (name, value) => localStorage.setItem(sessionKey(name), value),
        removeItem: (name) => localStorage.removeItem(sessionKey(name)),
      })),
      skipHydration: import.meta.env.DEV,
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
